import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Trash2, Loader2, ArrowLeft, Repeat, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Full pathway-meal edit dialog (beta feedback Feb 2026): athletes want to
// tweak the ingredients of a picked pathway meal before logging — swap oat
// milk → semi-skimmed, add whey scoop, adjust the oats portion. Opens with
// the planned meal's items pre-loaded (or a synthesized single row when
// the pathway only shipped meal-level macros). Every row is
// portion-editable, individually swap-able (opens search overlay) and
// removable. "Add ingredient" opens the same search overlay in add-mode.
// Save → POST /tracker/log/batch (existing endpoint) so every logged row
// is subsequently editable via the pencil icon like any own-food entry.

const scale = (per100, grams) => Math.round(((per100 || 0) * grams) / 100 * 10) / 10;
const r1 = (x) => Math.round((x || 0) * 10) / 10;
const clone = (x) => JSON.parse(JSON.stringify(x || {}));

// Rough grams-equivalent for a planned ingredient — used to split the
// meal-level macros across rows so each ingredient starts with a
// plausible per-row kcal/protein/carb/fat. Athletes tweak from there.
function ingredientGrams(ing) {
  const q = Number(ing?.qty || 0);
  if (!q) return 0;
  const u = String(ing?.unit || "").toLowerCase().trim();
  if (u === "g" || u === "ml") return q;
  if (u === "kg" || u === "l") return q * 1000;
  if (u === "pcs" || u === "pc" || u === "piece" || u === "pieces") return q * 100;
  if (u === "tbsp") return q * 15;
  if (u === "tsp") return q * 5;
  if (u === "cup") return q * 240;
  if (u === "oz") return q * 28;
  if (u === "slice" || u === "slices") return q * 30;
  if (u === "scoop" || u === "scoops") return q * 30;
  if (u === "serving" || u === "servings") return q * 100;
  return q * 100;
}

function itemsFromPlanned(planned) {
  if (!planned) return [];
  // 1) Preferred: pathway ships full per-ingredient macros in .items
  if (Array.isArray(planned.items) && planned.items.length) {
    const anyMacros = planned.items.some((it) =>
      (it.kcal || 0) > 0 || (it.protein || 0) > 0 || (it.carb || 0) > 0 || (it.fat || 0) > 0);
    if (anyMacros) {
      return planned.items.map((it) => ({
        name: it.name || planned.name || "Ingredient",
        qty: Number(it.qty || 1),
        unit: it.unit || "serving",
        kcal: Math.round(it.kcal || 0),
        protein: r1(it.protein || 0),
        carb: r1(it.carb || 0),
        fat: r1(it.fat || 0),
      }));
    }
  }
  // 2) Fallback: expand from .ingredients (name+qty+unit only) and split
  //    the meal's total macros proportionally by grams-equivalent so
  //    every ingredient shows up as its own editable row.
  const ingredients = Array.isArray(planned.ingredients) ? planned.ingredients : (Array.isArray(planned.items) ? planned.items : null);
  if (ingredients && ingredients.length) {
    const masses = ingredients.map(ingredientGrams);
    const total = masses.reduce((a, b) => a + b, 0);
    const evenShare = 1 / ingredients.length;
    return ingredients.map((ing, i) => {
      const share = total > 0 ? (masses[i] / total) : evenShare;
      return {
        name: ing.name || "Ingredient",
        qty: Number(ing.qty || 1),
        unit: ing.unit || "serving",
        kcal: Math.round((planned.kcal || 0) * share),
        protein: r1((planned.protein || 0) * share),
        carb: r1((planned.carb || 0) * share),
        fat: r1((planned.fat || 0) * share),
      };
    });
  }
  // 3) Ultimate fallback: single row using meal totals
  return [{
    name: planned.name || "Planned meal",
    qty: 1, unit: "serving",
    kcal: Math.round(planned.kcal || 0),
    protein: r1(planned.protein || 0),
    carb: r1(planned.carb || 0),
    fat: r1(planned.fat || 0),
  }];
}

export default function PathwayMealEditDialog({ open, onOpenChange, date, meal, planned, onLogged }) {
  const [items, setItems] = useState([]);
  const [swapIdx, setSwapIdx] = useState(null); // null = search closed, -1 = add mode, N = replacing index N
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setItems(itemsFromPlanned(planned));
      setSwapIdx(null); setQ(""); setResults([]);
    }
  }, [open, planned]);

  const runSearch = async (query) => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/tracker/search?q=${encodeURIComponent(query.trim())}`);
      setResults(data.results || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const pickSearchResult = (food) => {
    // Food comes as per-100g values (kcal/protein/carb/fat) with a
    // sensible default 100g portion. Athletes can then adjust qty inline.
    const grams = food.perServing?.grams || 100;
    const newRow = {
      name: food.name,
      qty: grams, unit: "g",
      kcal: scale(food.kcal, grams),
      protein: scale(food.protein, grams),
      carb: scale(food.carb, grams),
      fat: scale(food.fat, grams),
      _per100: { kcal: food.kcal, protein: food.protein, carb: food.carb, fat: food.fat },
    };
    setItems((prev) => {
      const next = [...prev];
      if (swapIdx === -1) next.push(newRow);
      else next[swapIdx] = newRow;
      return next;
    });
    setSwapIdx(null); setQ(""); setResults([]);
  };

  const setQty = (idx, val) => {
    const qty = Math.max(0, Number(val) || 0);
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      // If this row was built from a search (has _per100 gram baseline),
      // autoscale macros against the new qty; otherwise treat qty as
      // a serving multiplier off the original planned macros.
      if (it._per100) {
        return {
          ...it, qty,
          kcal: scale(it._per100.kcal, qty),
          protein: scale(it._per100.protein, qty),
          carb: scale(it._per100.carb, qty),
          fat: scale(it._per100.fat, qty),
        };
      }
      // Non-scalable rows keep macros static; athlete can still edit
      // qty for portion-multiplier semantics on log_batch downstream.
      return { ...it, qty };
    }));
  };

  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const totals = items.reduce(
    (t, it) => ({
      kcal: t.kcal + (it.kcal || 0),
      protein: t.protein + (it.protein || 0),
      carb: t.carb + (it.carb || 0),
      fat: t.fat + (it.fat || 0),
    }), { kcal: 0, protein: 0, carb: 0, fat: 0 });

  const canSave = items.length > 0 && !saving;
  const save = async () => {
    setSaving(true);
    try {
      const payloadItems = items.map((it) => {
        const { _per100, ...rest } = it;
        return {
          ...rest,
          kcal: Math.round(rest.kcal || 0),
          protein: r1(rest.protein), carb: r1(rest.carb), fat: r1(rest.fat),
        };
      });
      await api.post("/tracker/log/batch", {
        date, meal, items: payloadItems,
        source: "pathway", recipe_name: planned?.name || null,
      });
      toast.success(`Logged ${planned?.name || "meal"} — tap the pencil on any row to tweak later`);
      onOpenChange(false);
      if (onLogged) onLogged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log meal");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pathway-edit-dialog" className="bg-[#141414] border border-[#E2E30A]/40 text-white max-w-lg p-0 gap-0 rounded-none max-h-[92vh] flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
          <DialogTitle className="text-white font-display text-lg uppercase tracking-tight leading-tight">
            {swapIdx !== null ? (swapIdx === -1 ? "Add ingredient" : "Swap ingredient") : planned?.name || "Edit meal"}
          </DialogTitle>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] font-bold mt-1">
            {swapIdx !== null ? "Search food database" : `Planned · ${meal || "meal"} — hit Save to log, or tweak first`}
          </p>
        </DialogHeader>
        {swapIdx === null ? (
          <>
            <div className="overflow-y-auto flex-1">
              {items.length === 0 && (
                <p className="text-sm text-zinc-400 px-5 py-8 text-center">All ingredients removed — tap "Add ingredient" to build the meal or Cancel.</p>
              )}
              {items.map((it, idx) => (
                <div key={idx} data-testid={`pathway-item-${idx}`} className="px-5 py-3 border-b border-white/5 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{it.name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Input data-testid={`pathway-qty-${idx}`} type="number" min="0" step="0.1"
                        value={it.qty} onChange={(e) => setQty(idx, e.target.value)}
                        className="h-8 w-20 bg-[#0F0F0F] border-white/15 rounded-none text-white tabular-nums text-sm px-2" />
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">{it.unit}</span>
                      <span className="ml-auto text-[10px] font-mono text-zinc-300 tabular-nums">
                        {Math.round(it.kcal)} kcal · <span className="text-[#4C9AFF]">P {r1(it.protein)}</span> · <span className="text-[#34C759]">C {r1(it.carb)}</span> · <span className="text-[#FF9500]">F {r1(it.fat)}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button data-testid={`pathway-swap-${idx}`} onClick={() => { setSwapIdx(idx); setQ(""); setResults([]); }}
                      title="Swap for a different food" className="p-1.5 border border-white/15 text-zinc-300 hover:text-[#E2E30A] hover:border-[#E2E30A]/40 transition-colors">
                      <Repeat className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`pathway-remove-${idx}`} onClick={() => remove(idx)}
                      title="Remove this ingredient" className="p-1.5 border border-white/15 text-zinc-300 hover:text-[#FF3B30] hover:border-[#FF3B30]/40 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <button data-testid="pathway-add-ingredient" onClick={() => { setSwapIdx(-1); setQ(""); setResults([]); }}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-[11px] uppercase tracking-wider text-[#E2E30A] hover:bg-[#E2E30A]/[0.06] border-b border-white/5 transition-colors">
                <Plus className="h-3.5 w-3.5" /> Add ingredient
              </button>
            </div>
            <div className="border-t border-white/10 px-5 py-3 bg-[#0F0F0F]">
              <div className="flex items-center justify-between text-[11px] font-mono text-zinc-300 tabular-nums mb-3">
                <span>Total</span>
                <span>{Math.round(totals.kcal)} kcal · <span className="text-[#4C9AFF]">P {r1(totals.protein)}</span> · <span className="text-[#34C759]">C {r1(totals.carb)}</span> · <span className="text-[#FF9500]">F {r1(totals.fat)}</span></span>
              </div>
              <div className="flex gap-2">
                <Button data-testid="pathway-cancel" onClick={() => onOpenChange(false)} variant="outline"
                  className="flex-1 rounded-none border-white/15 text-white hover:bg-white/5">Cancel</Button>
                <Button data-testid="pathway-save" onClick={save} disabled={!canSave}
                  className="flex-1 rounded-none bg-[#E2E30A] hover:bg-[#c8c908] text-black font-bold uppercase tracking-wider">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Log meal</>}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="px-5 pt-4 pb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <button data-testid="pathway-search-back" onClick={() => { setSwapIdx(null); setQ(""); setResults([]); }}
                  className="p-1.5 border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 transition-colors">
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <div className="flex-1 flex items-center gap-2 border border-white/15 bg-[#0F0F0F] px-2 py-1.5">
                  <Search className="h-3.5 w-3.5 text-zinc-400" />
                  <input autoFocus data-testid="pathway-search-input" value={q}
                    onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }}
                    placeholder="Search 'semi skimmed milk', 'whey protein'…"
                    className="flex-1 bg-transparent border-0 outline-none text-sm text-white placeholder:text-zinc-500" />
                  {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
                </div>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {!q.trim() && (
                <p className="text-xs text-zinc-400 px-5 py-6 text-center">Type at least 2 letters. Results include our SPC food database + Open Food Facts.</p>
              )}
              {q.trim() && !searching && results.length === 0 && (
                <p className="text-xs text-zinc-400 px-5 py-6 text-center">No matches — try a broader term.</p>
              )}
              {results.map((food, i) => (
                <button key={food.code || food.name + i} data-testid={`pathway-search-result-${i}`}
                  onClick={() => pickSearchResult(food)}
                  className="w-full text-left px-5 py-2.5 border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <p className="text-sm text-white truncate">{food.name}</p>
                  <p className="text-[10px] font-mono text-zinc-400 mt-0.5">
                    {food.brand ? <span className="text-zinc-300">{food.brand} · </span> : null}
                    {Math.round(food.kcal || 0)} kcal · P {r1(food.protein)} · C {r1(food.carb)} · F {r1(food.fat)} per 100{food.unit || "g"}
                  </p>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
